// Turns figures and teacher-uploaded pictures into image data for the Word export.
// Figures are embedded as SVG (sharp when printed) with a PNG fallback for older Word versions.

import { renderFigureSvg } from './assessmentFigures';

export const MAX_DOCUMENT_IMAGE_WIDTH = 560;
const MAX_UPLOAD_EDGE = 1600;

function loadImage(source) {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('The image could not be read.'));
        image.src = source;
    });
}

function canvasBytes(canvas, type, quality) {
    return new Promise((resolve, reject) => canvas.toBlob(async (blob) => {
        if (!blob) {
            reject(new Error('The image could not be converted.'));
            return;
        }
        resolve(new Uint8Array(await blob.arrayBuffer()));
    }, type, quality));
}

export async function rasterizeSvg(svg, width, height, scale = 2) {
    const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
    try {
        const image = await loadImage(url);
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(width * scale);
        canvas.height = Math.round(height * scale);
        const context = canvas.getContext('2d');
        context.fillStyle = '#fff';
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        return await canvasBytes(canvas, 'image/png');
    } finally {
        URL.revokeObjectURL(url);
    }
}

export function fitImageSize(width, height, maxWidth = MAX_DOCUMENT_IMAGE_WIDTH) {
    const ratio = Math.min(1, maxWidth / width);
    return { width: Math.round(width * ratio), height: Math.round(height * ratio) };
}

// Reads a teacher's picture, shrinks large photos and re-encodes it as PNG or JPEG, which every
// Word version can show. The result stays in the browser; it is not uploaded anywhere.
export async function readQuestionImage(file) {
    if (!file || !/^image\//.test(file.type)) throw new Error('Choose a picture file (PNG, JPG, GIF or WEBP).');
    const url = URL.createObjectURL(file);
    try {
        const image = await loadImage(url);
        const ratio = Math.min(1, MAX_UPLOAD_EDGE / Math.max(image.naturalWidth, image.naturalHeight));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(image.naturalWidth * ratio));
        canvas.height = Math.max(1, Math.round(image.naturalHeight * ratio));
        const context = canvas.getContext('2d');
        context.fillStyle = '#fff';
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        const type = file.type === 'image/png' ? 'png' : 'jpg';
        return {
            dataUrl: canvas.toDataURL(type === 'png' ? 'image/png' : 'image/jpeg', 0.88),
            type,
            width: canvas.width,
            height: canvas.height,
            name: file.name
        };
    } finally {
        URL.revokeObjectURL(url);
    }
}

function utf8Bytes(text) {
    if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(text);
    const binary = unescape(encodeURIComponent(text));
    return Uint8Array.from(binary, (char) => char.charCodeAt(0));
}

export function dataUrlBytes(dataUrl) {
    const base64 = String(dataUrl).split(',')[1] || '';
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
    return bytes;
}

// Prepares every figure and uploaded picture of the given questions for the Word builder.
// Returns a WeakMap from question object to { figure, image }, as ImageRun-ready data.
export async function prepareQuestionVisuals(questions, { rasterize = rasterizeSvg, notToScale } = {}) {
    const visuals = new WeakMap();
    for (const question of questions) {
        const entry = {};
        if (question.figure) {
            const rendered = renderFigureSvg(question.figure, { notToScale });
            if (rendered) {
                entry.figure = {
                    svg: utf8Bytes(rendered.svg),
                    png: await rasterize(rendered.svg, rendered.width, rendered.height),
                    ...fitImageSize(rendered.width, rendered.height)
                };
            }
        }
        if (question.image?.dataUrl) {
            entry.image = {
                data: dataUrlBytes(question.image.dataUrl),
                type: question.image.type === 'png' ? 'png' : 'jpg',
                ...fitImageSize(question.image.width, question.image.height, 480)
            };
        }
        if (entry.figure || entry.image) visuals.set(question, entry);
    }
    return visuals;
}
